import React, { useState, useEffect } from 'react';
import { jobListings as mockJobs } from '../mockData';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Eye } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import * as api from '../lib/api';

const CompanyJobsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await api.getJobs();
        const list = Array.isArray(data) ? data : (data.jobs || []);
        setJobs(list.slice(0, 5));
      } catch { setJobs(mockJobs.slice(0, 3)); } finally { setLoading(false); }
    };
    load();
  }, [user]);

  const handleDelete = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job?')) return;
    try { await api.deleteJob(jobId); setJobs(jobs.filter(j => (j.id || j.job_id) !== jobId)); toast({ title: 'Job deleted' }); } catch { toast({ title: 'Delete failed', variant: 'destructive' }); }
  };

  const getPayRange = (j) => j.payRange || j.pay_range || { min: 0, max: 0 };

  return (
    <div className="min-h-screen bg-[#F9FBF5] pt-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">My Job Postings</h1>
          <Button onClick={() => navigate('/company/post-job')} className="bg-[#0A3900] hover:bg-[#1A5C0A]"><Plus className="w-4 h-4 mr-2" />Post New Job</Button>
        </div>

        {loading ? <div className="text-center py-12 text-gray-500">Loading...</div> : jobs.length === 0 ? (
          <Card className="p-12 text-center"><h2 className="text-xl font-semibold text-gray-900 mb-2">No jobs posted yet</h2><p className="text-gray-600 mb-4">Create your first job posting.</p><Button onClick={() => navigate('/company/post-job')} className="bg-[#0A3900] hover:bg-[#1A5C0A]">Post a Job</Button></Card>
        ) : (
          <div className="space-y-4">
            {jobs.map((job) => {
              const pay = getPayRange(job);
              return (
                <Card key={job.id || job.job_id} className="p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{job.title}</h3>
                        <Badge className={job.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>{job.status || 'active'}</Badge>
                      </div>
                      <p className="text-gray-600 mb-2">${pay.min}-${pay.max}/hr · {job.location} · {job.type}</p>
                      <div className="flex flex-wrap gap-2">{(job.skills || []).slice(0, 4).map((s, i) => (<Badge key={i} variant="secondary">{s}</Badge>))}</div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button variant="outline" size="sm" onClick={() => navigate(`/jobs/${job.id || job.job_id}`)}><Eye className="w-4 h-4" /></Button>
                      <Button variant="outline" size="sm"><Edit className="w-4 h-4" /></Button>
                      <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(job.id || job.job_id)}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyJobsPage;
